// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';

export const WEIGHT_STEPS = [25, 50, 75, 100, 150] as const;

const QUADRANT_INDEXES = [0, 1, 2, 3] as const;

type GameWeightControlProps = {
  weightPercent: number;
  onPress: () => void;
  selected?: boolean;
  centered?: boolean;
  showLabel?: boolean;
  size?: 'default' | 'compact';
  disabled?: boolean;
  accessibilityLabel?: string;
  /** Optional testID for the weight button, so E2E can cycle a specific game's merit weight. */
  testID?: string;
};

export default function GameWeightControl({
  weightPercent,
  onPress,
  selected = false,
  centered = false,
  showLabel = true,
  size = 'default',
  disabled = false,
  accessibilityLabel,
  testID,
}: GameWeightControlProps) {
  const normalizedPercent = WEIGHT_STEPS.includes(weightPercent as (typeof WEIGHT_STEPS)[number]) ? weightPercent : 100;
  const activeCount = Math.max(0, Math.min(4, Math.round(normalizedPercent / 25)));
  const boosted = normalizedPercent > 100;
  const compact = size === 'compact';

  return (
    <View style={[styles.weightRow, centered && styles.weightRowCentered, compact && styles.weightRowCompact]}>
      {showLabel ? <Text style={styles.weightLabel}>{t('tournaments.gameWeightLabel')}</Text> : null}
      <Pressable
        testID={testID}
        style={[
          styles.weightCircle,
          compact && styles.weightCircleCompact,
          selected && styles.weightCircleSelected,
        ]}
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? `${t('tournaments.gameWeightLabel')} ${normalizedPercent}%`}
      >
        <View style={[styles.weightQuad, compact && styles.weightQuadCompact]}>
          {QUADRANT_INDEXES.map((index) => {
            const quadrantStyle =
              index === 0
                ? styles.weightQuadTopLeft
                : index === 1
                  ? styles.weightQuadTopRight
                  : index === 2
                    ? styles.weightQuadBottomRight
                    : styles.weightQuadBottomLeft;
            const isActive = index < activeCount;
            return (
              <View
                key={index}
                style={[
                  styles.weightQuadCell,
                  compact && styles.weightQuadCellCompact,
                  quadrantStyle,
                  isActive ? (boosted ? styles.weightQuadBoosted : styles.weightQuadActive) : styles.weightQuadInactive,
                ]}
              />
            );
          })}
        </View>
        <Text style={[styles.weightCircleText, compact && styles.weightCircleTextCompact]}>{normalizedPercent}%</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  weightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 6,
  },
  weightRowCentered: {
    justifyContent: 'center',
  },
  weightRowCompact: {
    marginTop: 0,
  },
  weightLabel: {
    color: '#F5F5DC',
    fontSize: 11,
    fontWeight: '700',
  },
  weightCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(47,138,78,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  weightCircleCompact: {
    width: 34,
    height: 34,
    borderRadius: 17,
  },
  weightCircleSelected: {
    backgroundColor: 'rgba(47,138,78,0.16)',
  },
  weightQuad: {
    width: 34,
    height: 34,
    borderRadius: 17,
    overflow: 'hidden',
    position: 'absolute',
  },
  weightQuadCompact: {
    width: 26,
    height: 26,
    borderRadius: 13,
  },
  weightQuadCell: {
    position: 'absolute',
    width: 17,
    height: 17,
  },
  weightQuadCellCompact: {
    width: 13,
    height: 13,
  },
  weightQuadActive: {
    backgroundColor: '#f59e0b',
  },
  weightQuadBoosted: {
    backgroundColor: '#ef6c00',
  },
  weightQuadInactive: {
    backgroundColor: 'rgba(42,119,66,0.24)',
  },
  weightQuadTopLeft: {
    top: 0,
    left: 0,
  },
  weightQuadTopRight: {
    top: 0,
    right: 0,
  },
  weightQuadBottomLeft: {
    bottom: 0,
    left: 0,
  },
  weightQuadBottomRight: {
    bottom: 0,
    right: 0,
  },
  weightCircleText: {
    color: '#F5F5DC',
    fontSize: 10,
    fontWeight: '700',
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  weightCircleTextCompact: {
    fontSize: 9,
  },
});
